/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 * @author Dominik Böck <d.boeck@techdivision.com>
 */

import * as Yup from 'yup';
import { useCallback } from 'react';
import { checkoutFormModel } from './FormModel';
import { isIbanValid, voucherMessage, isVoucherValid } from '../../../Signals/formStateSignal';

const {
  formField: {
    studioId,
    rateBundle,
    rateBundleTerm,
    gender,
    firstName,
    lastName,
    dateOfBirth,
    street,
    houseNumber,
    postCode,
    city,
    email,
    phone,
    accountOwner,
    iban,
    paymentTerms,
    voucherCode,
  },
} = checkoutFormModel;

/**
 * dynamic form validation schema
 */
export const useCheckoutFormValidatorGenerator = () => {
  const getStudioSchema = () => ({
    [studioId.name]: Yup.string().required(`${studioId.requiredErrorMsg}`),
  });

  const getContractSchema = () => ({
    [rateBundle.name]: Yup.object().required(),
    [rateBundleTerm.name]: Yup.object().required(),
  });

  const getPersonalDataSchema = () => ({
    [firstName.name]: Yup.string()
      .required(`${firstName.requiredErrorMsg}`)
      .min(2, 'Dieses Feld muss mindestens 2 Zeichen enthalten'),
    [lastName.name]: Yup.string()
      .required(`${lastName.requiredErrorMsg}`)
      .min(2, 'Dieses Feld muss mindestens 3 Zeichen enthalten'),
    [gender.name]: Yup.string().required(`${gender.requiredErrorMsg}`),
    [street.name]: Yup.string().required(`${street.requiredErrorMsg}`),
    [houseNumber.name]: Yup.string().required(`${houseNumber.requiredErrorMsg}`),
    [postCode.name]: Yup.number()
      .positive(`${postCode.requiredErrorMsg}`)
      .required(`${postCode.requiredErrorMsg}`)
      .typeError(`${postCode.requiredErrorMsg}`),
    [city.name]: Yup.string().required(`${city.requiredErrorMsg}`),
    [email.name]: Yup.string()
      .required(`${email.requiredErrorMsg}`)
      .email('Bitte geben Sie eine gültige E-Mail-Adresse an'),
    [phone.name]: Yup.string().matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      'Telefonnummer ist nicht valide',
    ),
    [dateOfBirth.name]: Yup.string().required(`${dateOfBirth.requiredErrorMsg}`),
  });

  const getPaymentSchema = (paymentChoice) => {
    switch (paymentChoice) {
      case 'DIRECT_DEBIT':
        return {
          [accountOwner.name]: Yup.string().required(`${accountOwner.requiredErrorMsg}`),
          [iban.name]: Yup.string()
            .required(`${iban.requiredErrorMsg}`)
            .test('iban-test', iban.notValidErrorMsg, () => isIbanValid.value),
          [paymentTerms.name]: Yup.bool().required(`${paymentTerms.requiredErrorMsg}`),
        };
      case 'CREDIT_CARD':
      default:
        return {};
    }
  };

  const getPreviewSchema = () => ({
    [voucherCode.name]: Yup.string().test('voucher-test', voucherMessage.value, () => isVoucherValid.value),
  });

  const generateSchema = useCallback(
    (initialFormValues) => [
      Yup.object().shape(getStudioSchema()),
      Yup.object().shape(getContractSchema()),
      Yup.object().shape(getPersonalDataSchema()),
      Yup.object().shape(getPaymentSchema(initialFormValues.paymentChoice)),
      Yup.object().shape(getPreviewSchema()),
    ],
    [],
  );
  return { generateSchema };
};
